import React, { useMemo, useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, MapPin, Sparkles, Users, Clock, User } from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, SKILLS, TEAM_SIZES, DURATIONS } from "@/data/site";
import { LEAD_FORM } from "@/constants/testIds";
import { submitLead } from "@/lib/api";
import { useT } from "@/i18n";

const STEP_ICONS = [MapPin, Sparkles, Users, Clock, User];

export default function LeadForm({ defaultCountry, sourcePage = "" }) {
  const t = useT();
  const [step, setStep] = useState(defaultCountry ? 1 : 0);
  const [data, setData] = useState({
    country: defaultCountry || "",
    skills: [],
    team_size: "",
    duration: "",
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // If defaultCountry changes (e.g. language toggles country name), update it
  useEffect(() => {
    if (defaultCountry) setData((d) => ({ ...d, country: defaultCountry }));
  }, [defaultCountry]);

  const canNext = useMemo(() => {
    if (step === 0) return !!data.country;
    if (step === 1) return data.skills.length > 0;
    if (step === 2) return !!data.team_size;
    if (step === 3) return !!data.duration;
    if (step === 4) return data.name.trim() && data.email.trim() && data.company.trim();
    return false;
  }, [step, data]);

  const toggleSkill = (slug) => {
    const exists = data.skills.includes(slug);
    setData((d) => ({
      ...d,
      skills: exists ? d.skills.filter((s) => s !== slug) : [...d.skills, slug],
    }));
  };

  const handleNext = () => {
    if (step < t.leadForm.stepLabels.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setErrorMsg("");
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        skills: data.skills.map((slug) => t.skills[slug] || slug),
        team_size: t.teamSizes[data.team_size] || data.team_size,
        duration: t.durations[data.duration] || data.duration,
        source_page: sourcePage,
      };
      await submitLead(payload);
      setSubmitted(true);
      toast.success(t.leadForm.toastSuccessTitle, {
        description: t.leadForm.toastSuccessDesc,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || t.leadForm.errorGeneric;
      setErrorMsg(typeof detail === "string" ? detail : t.leadForm.errorGeneric);
      toast.error(t.leadForm.toastErrorTitle, { description: typeof detail === "string" ? detail : "" });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid={LEAD_FORM.success} className="nx-card rounded-2xl p-10 md:p-14 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#0057FF] to-[#38D8FF] flex items-center justify-center mb-6">
          <Check className="w-7 h-7 text-white" />
        </div>
        <h3 className="nx-heading text-2xl md:text-3xl font-bold">{t.leadForm.successTitle}</h3>
        <p className="mt-4 text-white/70 max-w-lg mx-auto leading-relaxed">{t.leadForm.successLede}</p>
      </div>
    );
  }

  return (
    <div data-testid={LEAD_FORM.root} className="nx-card rounded-2xl p-6 sm:p-8 md:p-10">
      <StepIndicator step={step} stepLabels={t.leadForm.stepLabels} />

      <div className="mt-8 min-h-[280px]">
        {step === 0 && (
          <StepCountry
            t={t}
            value={data.country}
            onChange={(v) => setData((d) => ({ ...d, country: v }))}
          />
        )}
        {step === 1 && (
          <StepSkills t={t} selected={data.skills} onToggle={toggleSkill} />
        )}
        {step === 2 && (
          <StepChoice
            options={TEAM_SIZES.map((o) => ({ slug: o.slug, label: t.teamSizes[o.slug] || o.label }))}
            value={data.team_size}
            onChange={(v) => setData((d) => ({ ...d, team_size: v }))}
            title={t.leadForm.teamSize.title}
            lede={t.leadForm.teamSize.lede}
            testIdPrefix="team-size"
          />
        )}
        {step === 3 && (
          <StepChoice
            options={DURATIONS.map((o) => ({ slug: o.slug, label: t.durations[o.slug] || o.label }))}
            value={data.duration}
            onChange={(v) => setData((d) => ({ ...d, duration: v }))}
            title={t.leadForm.duration.title}
            lede={t.leadForm.duration.lede}
            testIdPrefix="duration"
          />
        )}
        {step === 4 && (
          <StepDetails t={t} data={data} onChange={(patch) => setData((d) => ({ ...d, ...patch }))} />
        )}
      </div>

      {errorMsg && (
        <div data-testid={LEAD_FORM.error} className="mt-6 text-sm text-rose-300">
          {errorMsg}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-white/10 pt-6">
        <button
          data-testid={LEAD_FORM.back}
          onClick={handleBack}
          disabled={step === 0 || submitting}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm text-white/75 hover:text-white border border-white/15 hover:border-white/30 transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" /> {t.leadForm.back}
        </button>

        {step < t.leadForm.stepLabels.length - 1 ? (
          <button
            data-testid={LEAD_FORM.next}
            onClick={handleNext}
            disabled={!canNext}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-[#0057FF] hover:bg-[#2F80FF] text-white nx-cta-glow disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {t.leadForm.next} <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid={LEAD_FORM.submit}
            onClick={handleSubmit}
            disabled={!canNext || submitting}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-[#0057FF] hover:bg-[#2F80FF] text-white nx-cta-glow disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {t.leadForm.sending}
              </>
            ) : (
              <>
                {t.leadForm.send} <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function StepIndicator({ step, stepLabels }) {
  return (
    <div data-testid={LEAD_FORM.stepIndicator} className="flex items-center gap-2 sm:gap-3 overflow-x-auto">
      {stepLabels.map((label, i) => {
        const active = i === step;
        const done = i < step;
        const Icon = STEP_ICONS[i] || MapPin;
        return (
          <React.Fragment key={i}>
            <div
              className={`flex items-center gap-2 text-xs sm:text-sm whitespace-nowrap ${
                active ? "text-white" : done ? "text-[#38D8FF]" : "text-white/40"
              }`}
            >
              <div
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border ${
                  active
                    ? "border-[#38D8FF] bg-[#38D8FF]/10"
                    : done
                    ? "border-[#38D8FF] bg-[#38D8FF]/20"
                    : "border-white/20"
                }`}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className="hidden sm:inline">{label}</span>
            </div>
            {i < stepLabels.length - 1 && (
              <div className={`h-px flex-1 min-w-[12px] ${i < step ? "bg-[#38D8FF]/60" : "bg-white/10"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StepCountry({ t, value, onChange }) {
  return (
    <div>
      <h3 className="nx-heading text-2xl md:text-3xl font-semibold">{t.leadForm.country.title}</h3>
      <p className="mt-2 text-white/65 text-sm">{t.leadForm.country.lede}</p>
      <div data-testid={LEAD_FORM.countrySelect} className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {COUNTRIES.map((c) => {
          const key = c.code === "uk" ? "united-kingdom" : c.code === "nl" ? "netherlands" : c.code === "de" ? "germany" : c.code === "ch" ? "switzerland" : "belgium";
          const displayName = t.countriesNav[key];
          const active = value === displayName;
          return (
            <button
              key={c.code}
              data-testid={`lead-form-country-${c.code}`}
              onClick={() => onChange(displayName)}
              className={`flex items-center gap-3 p-4 rounded-xl border text-left transition ${
                active
                  ? "border-[#38D8FF] bg-gradient-to-br from-[#0057FF]/30 to-[#38D8FF]/10"
                  : "border-white/12 hover:border-white/30 bg-white/[0.02]"
              }`}
            >
              <span className="text-2xl">{c.flag}</span>
              <span className="text-sm font-medium">{displayName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepSkills({ t, selected, onToggle }) {
  return (
    <div>
      <h3 className="nx-heading text-2xl md:text-3xl font-semibold">{t.leadForm.skills.title}</h3>
      <p className="mt-2 text-white/65 text-sm">{t.leadForm.skills.lede}</p>
      <div className="mt-6 flex flex-wrap gap-2.5">
        {SKILLS.map((s) => {
          const active = selected.includes(s.slug);
          return (
            <button
              key={s.slug}
              data-testid={LEAD_FORM.skillChip(s.slug)}
              onClick={() => onToggle(s.slug)}
              className={`nx-chip ${active ? "is-selected" : ""}`}
              type="button"
            >
              {active && <Check className="w-3.5 h-3.5" />}
              {t.skills[s.slug] || s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepChoice({ options, value, onChange, title, lede, testIdPrefix }) {
  return (
    <div>
      <h3 className="nx-heading text-2xl md:text-3xl font-semibold">{title}</h3>
      {lede && <p className="mt-2 text-white/65 text-sm">{lede}</p>}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((o) => {
          const active = value === o.slug;
          return (
            <button
              key={o.slug}
              data-testid={`lead-form-${testIdPrefix}-${o.slug}`}
              onClick={() => onChange(o.slug)}
              className={`p-5 rounded-xl border text-left transition ${
                active
                  ? "border-[#38D8FF] bg-gradient-to-br from-[#0057FF]/30 to-[#38D8FF]/10"
                  : "border-white/12 hover:border-white/30 bg-white/[0.02]"
              }`}
            >
              <div className="text-sm font-medium">{o.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepDetails({ t, data, onChange }) {
  return (
    <div>
      <h3 className="nx-heading text-2xl md:text-3xl font-semibold">{t.leadForm.details.title}</h3>
      <p className="mt-2 text-white/65 text-sm">{t.leadForm.details.lede}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label={t.leadForm.details.name}>
          <input
            data-testid={LEAD_FORM.name}
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t.leadForm.details.namePh}
            className="nx-input"
          />
        </Field>
        <Field label={t.leadForm.details.email}>
          <input
            data-testid={LEAD_FORM.email}
            value={data.email}
            type="email"
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder={t.leadForm.details.emailPh}
            className="nx-input"
          />
        </Field>
        <Field label={t.leadForm.details.company} className="sm:col-span-2">
          <input
            data-testid={LEAD_FORM.company}
            value={data.company}
            onChange={(e) => onChange({ company: e.target.value })}
            placeholder={t.leadForm.details.companyPh}
            className="nx-input"
          />
        </Field>
        <Field label={t.leadForm.details.message} className="sm:col-span-2">
          <textarea
            data-testid={LEAD_FORM.message}
            value={data.message}
            onChange={(e) => onChange({ message: e.target.value })}
            rows={4}
            placeholder={t.leadForm.details.messagePh}
            className="nx-input resize-none"
          />
        </Field>
      </div>
      <style>{`
        .nx-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.12);
          color: #fff;
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14px;
          outline: none;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .nx-input::placeholder { color: rgba(255,255,255,0.4); }
        .nx-input:focus {
          border-color: #38D8FF;
          background: rgba(56,216,255,0.06);
          box-shadow: 0 0 0 3px rgba(56,216,255,0.18);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-2">{label}</div>
      {children}
    </label>
  );
}
